import React, { useEffect, useRef, useState } from "react";
import Title from "../common/Title";
import { FaDatabase } from "react-icons/fa";
import { MdGppBad, MdGppGood, MdSystemUpdateAlt, MdFileDownload, MdContentCopy, MdArticle } from "react-icons/md";
import Utils from "../../utils";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Button, Spinner, Alert, Badge, Card, Row, Col } from "react-bootstrap";
import { clientConnectorActions } from "../../actions/clientConnector.actions";
import { clientConnectorService } from "../../services/clientConnector.service";
import { useDispatch, useSelector } from "react-redux";
import SkeletonForm from "../common/SkeletonForm";
import SecretInput from "../common/SecretInput";
import RunbookSystemDropdown from "../runbook/RunbookSystemDropdown";
import SystemIcon from "../runbookSystem/SystemIcon";
import { secretService } from "../../services/secret.service";
import { usePermissions } from "../../hooks/usePermissions";
import { useToast } from "../../contexts/ToastContext";

// OnPrem connectors run a locally installed agent, so "Test Connection" is an async round-trip
// (dispatch -> agent probes -> result posted back -> we poll for the status). These controls live
// on the edit form and are shown only when the connector's system is OnPrem.
const ONPREM_SYSTEMS = ["OnPremiseSQL", "OnPremiseFiles"];
const isOnPremSystem = (system) => ONPREM_SYSTEMS.includes(system);

// Terminal states the agent (via OnPremResponseService) writes back once a test finishes.
const RESOLVED_TEST_STATUSES = ["Success", "Failed", "NoResponse"];
const ONPREM_TEST_TIMEOUT_MS = 60000;
const ONPREM_TEST_POLL_MS = 4000;

// "Download Server Log" round-trip (dispatch -> agent collects Event Log + posts it -> we poll for
// the download link). Collecting + gzipping + posting a log can take longer than a connectivity
// probe, so allow a slightly longer window; the cloud's stale-log sweep backstops at 15 min.
const ONPREM_LOG_TIMEOUT_MS = 90000;
const ONPREM_LOG_POLL_MS = 4000;

// DATETIME comes back from the API as a UTC value without an offset marker; treat it as UTC.
const formatTestTime = (value) => {
  if (!value) return "";
  const utc = /[zZ]|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`;
  const date = new Date(utc);
  return isNaN(date.getTime()) ? "" : date.toLocaleString();
};

const lastTestBadge = (status) => {
  if (!status) return <Badge bg="light" text="dark">Not tested</Badge>;
  const config = {
    Pending: { bg: "warning", text: "dark", label: "Pending" },
    Success: { bg: "success", label: "Success" },
    Failed: { bg: "danger", label: "Failed" },
    NoResponse: { bg: "secondary", label: "No response" },
  }[status] || { bg: "secondary", label: status };
  return (
    <Badge bg={config.bg} text={config.text}>
      {config.label}
    </Badge>
  );
};

function ConnectorForm(props) {
  const { hideTitle, presetConnectorId, onSaveSuccess, onCancel } = props;
  const { hasPermission } = usePermissions(props.user);
  const { showToast } = useToast();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const connectorInitialState = {
    connectorId: "",
    system: "",
    loginEndpoint: "",
    senderId: "",
    senderPassword: "",
    userId: "",
    userPassword: "",
    companyId: "",
    oAuthClientId: "",
    clientSecret: "",
    tenantId: "",
    description: "",
    refreshToken: "",
    scope: "",
    delimiter: "",
    version: "",
    headerRow: false,
  };
  const [connector, setConnector] = useState(connectorInitialState);
  const [selectedSystemConfig, setSelectedSystemConfig] = useState(null);
  const clientConnectorReducer = useSelector((state) => state.clientConnector);
  const runbookSystemReducer = useSelector((state) => state.runbookSystem);

  // Phase of an in-flight OnPrem test: "polling" (waiting on the agent), "timeout" (no reply in
  // 60s), or "resolved". Null when idle.
  const [onPremTestPhase, setOnPremTestPhase] = useState(null);
  const [pushDismissed, setPushDismissed] = useState(false);
  const [downloadingInstaller, setDownloadingInstaller] = useState(false);
  const [downloadInstallerError, setDownloadInstallerError] = useState(null);
  const [togglingAutoUpdate, setTogglingAutoUpdate] = useState(false);
  const [installerLinkCopied, setInstallerLinkCopied] = useState(false);
  const [installerLinkExpiry, setInstallerLinkExpiry] = useState(null);
  const [copyingLink, setCopyingLink] = useState(false);
  // Phase of an in-flight "Download Server Log": "polling" | "ready" | "timeout" | "failed". Null idle.
  const [logPhase, setLogPhase] = useState(null);
  const [logError, setLogError] = useState(null);
  const pollIntervalRef = useRef(null);
  const pollDeadlineRef = useRef(0);
  const logPollRef = useRef(null);
  const logDeadlineRef = useRef(0);
  // One-shot: makes the next connector fetch merge only the OnPrem read fields (version + test
  // status) instead of full-replacing, so a refresh after a test/update reflects the agent's
  // newly-reported version without clobbering unsaved edits.
  const refreshVersionRef = useRef(false);

  const isOnPrem = isOnPremSystem(connector.system);

  const buildConnectorQuery = () => {
    const queryParams = Utils.getQueryStringParams(location);
    queryParams["clientId"] = props.user.clientId;
    queryParams["clientGuid"] = props.user.clientGuid;
    return queryParams;
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const startOnPremTestPolling = () => {
    stopPolling();
    pollDeadlineRef.current = Date.now() + ONPREM_TEST_TIMEOUT_MS;
    pollIntervalRef.current = setInterval(() => {
      if (Date.now() > pollDeadlineRef.current) {
        setOnPremTestPhase("timeout");
        stopPolling();
        return;
      }
      dispatch(clientConnectorActions.get(buildConnectorQuery()));
    }, ONPREM_TEST_POLL_MS);
  };

  const handleTestConnection = () => {
    // Clear the other action's stale result so only this test's outcome shows in the card
    // (e.g. a lingering "Update pushed" alert should disappear when a test is started).
    setPushDismissed(true);
    // OnPrem test is async: the dispatch queues the probe and marks the status Pending; we then
    // poll the connector until the agent posts a result (or we hit the 60s timeout).
    setConnector((c) => ({
      ...c,
      onPremConnTestStatus: "Pending",
      onPremConnTestMessage: "",
      onPremConnTestTime: null,
    }));
    dispatch(
      clientConnectorActions.testConnectorAuth({
        connectorId: connector.connectorNumber,
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
      })
    );
    setOnPremTestPhase("polling");
    startOnPremTestPolling();
  };

  const installerErrorText = (err) =>
    err?.response?.status === 404
      ? "No active installer is published for this environment yet."
      : "Failed to get the installer link. Please try again.";

  const handleDownloadInstaller = async () => {
    // Fetch a short-lived SAS URL for the active installer, then let the BROWSER download it
    // directly (native progress bar) rather than proxying the ~47 MB through the app. Super-user.
    setDownloadInstallerError(null);
    setDownloadingInstaller(true);
    try {
      const res = await clientConnectorService.getOnPremInstallerUrl();
      const link = document.createElement("a");
      link.href = res.data.downloadUrl;
      link.target = "_blank";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setDownloadInstallerError(installerErrorText(err));
    } finally {
      setDownloadingInstaller(false);
    }
  };

  const handleCopyInstallerLink = async () => {
    // Copies the same short-lived SAS URL so the admin can send it to a client to self-download.
    // Uses its own loading flag so it doesn't spin the Download button.
    setDownloadInstallerError(null);
    setCopyingLink(true);
    try {
      // forCopy=true → a longer-lived link (the client opens it later). Surface the expiry so the
      // admin can tell the client when it dies — an expired link fails at Azure with a raw 403 we
      // can't intercept (the download is browser -> blob, not through the portal).
      const res = await clientConnectorService.getOnPremInstallerUrl(true);
      await navigator.clipboard.writeText(res.data.downloadUrl);
      setInstallerLinkExpiry(res.data.expiresUtc || null);
      setInstallerLinkCopied(true);
      setTimeout(() => setInstallerLinkCopied(false), 5000);
    } catch (err) {
      setDownloadInstallerError(installerErrorText(err));
    } finally {
      setCopyingLink(false);
    }
  };

  const handleToggleAutoUpdate = async (e) => {
    // The switch shows "enabled"; the stored flag is "disabled" (the opt-out). Applies to the
    // client's whole OnPrem agent.
    const disabled = !e.target.checked;
    setTogglingAutoUpdate(true);
    try {
      await clientConnectorService.setOnPremAutoUpdate({
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
        disabled,
      });
      setConnector((c) => ({ ...c, onPremAutoUpdateDisabled: disabled }));
    } catch {
      // Leave the switch reflecting the unchanged connector state on failure.
    } finally {
      setTogglingAutoUpdate(false);
    }
  };

  const handlePushUpdate = () => {
    // Clear the other action's stale result so only this push's outcome shows in the card:
    // stop any in-flight test poll and hide the previous test's status lines.
    stopPolling();
    setOnPremTestPhase(null);
    setPushDismissed(false);
    dispatch(
      clientConnectorActions.pushOnPremUpdate({
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
      })
    );
  };

  const stopLogPolling = () => {
    if (logPollRef.current) {
      clearInterval(logPollRef.current);
      logPollRef.current = null;
    }
  };

  const triggerBrowserDownload = (downloadUrl) => {
    // Let the BROWSER download the gzip directly from the short-lived SAS URL (native progress),
    // rather than streaming it through the app.
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.target = "_blank";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const startLogPolling = () => {
    stopLogPolling();
    logDeadlineRef.current = Date.now() + ONPREM_LOG_TIMEOUT_MS;
    let busy = false; // guard against overlapping polls if a request runs long
    logPollRef.current = setInterval(async () => {
      if (Date.now() > logDeadlineRef.current) {
        setLogPhase("timeout");
        stopLogPolling();
        return;
      }
      if (busy) return;
      busy = true;
      try {
        const res = await clientConnectorService.getOnPremLogUrl(
          props.user.clientId,
          props.user.clientGuid
        );
        const data = res?.data || res;
        if (data?.status === "Ready" && data?.downloadUrl) {
          stopLogPolling();
          setLogPhase("ready");
          triggerBrowserDownload(data.downloadUrl);
        } else if (data?.status === "Failed" || data?.status === "NoResponse") {
          stopLogPolling();
          setLogPhase("failed");
          setLogError(
            data.status === "NoResponse"
              ? "The connector did not respond. It may be offline."
              : "The connector could not collect its log."
          );
        }
        // Pending / None -> keep polling until the agent responds or we time out.
      } catch {
        // Transient poll error -> keep polling until the deadline.
      } finally {
        busy = false;
      }
    }, ONPREM_LOG_POLL_MS);
  };

  const handleDownloadServerLog = async () => {
    // Async round-trip like Test Connection: dispatch the collect request (marks the agent's log
    // status Pending), then poll until the agent posts its log and the cloud hands back a download
    // link. Super-user only.
    setLogError(null);
    setLogPhase("polling");
    try {
      await clientConnectorService.collectOnPremLog({
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
      });
    } catch {
      setLogPhase("failed");
      setLogError("Failed to dispatch the log request. Please try again.");
      return;
    }
    startLogPolling();
  };

  useEffect(() => {
    // Hide any lingering "Update pushed" alert when navigating into / between connectors. The push
    // success flag lives in redux (survives navigation), so without this it would reappear on a
    // different connector or when returning to the page.
    setPushDismissed(true);

    let queryParams = Utils.getQueryStringParams(location);
    const connectorId = presetConnectorId ?? queryParams["connectorId"];
    if (connectorId) {
      queryParams["connectorId"] = connectorId;
      queryParams["clientId"] = props.user.clientId;
      queryParams["clientGuid"] = props.user.clientGuid;
      dispatch(clientConnectorActions.get(queryParams));
    } else {
      setConnector(connectorInitialState);
    }
    // eslint-disable-next-line
  }, [location, presetConnectorId]);

  useEffect(() => {
    let queryParams = Utils.getQueryStringParams(location);
    const connectorId = presetConnectorId ?? queryParams["connectorId"];
    const data = clientConnectorReducer.paginatedResult?.data?.[0];

    if (!connectorId || !data) return;

    if (pollIntervalRef.current || refreshVersionRef.current) {
      // A test poll is in flight (or a one-shot post-action refresh) — merge ONLY the OnPrem read
      // fields so we don't clobber any unsaved edits, and return the same reference when nothing
      // changed so React skips the re-render (no flicker while the status stays Pending).
      refreshVersionRef.current = false;
      setConnector((c) => {
        if (
          c.onPremConnTestStatus === data.onPremConnTestStatus &&
          c.onPremConnTestMessage === data.onPremConnTestMessage &&
          c.onPremConnTestTime === data.onPremConnTestTime &&
          c.onPremConnectorVersion === data.onPremConnectorVersion
        ) {
          return c;
        }
        return {
          ...c,
          onPremConnTestStatus: data.onPremConnTestStatus,
          onPremConnTestMessage: data.onPremConnTestMessage,
          onPremConnTestTime: data.onPremConnTestTime,
          // The agent reports its version on the same check-in as the test result, so refresh it
          // in-place too (otherwise the version only updated after navigating away and back).
          onPremConnectorVersion: data.onPremConnectorVersion,
        };
      });
    } else {
      // Normal load/refresh — populate the whole form from the requested connector.
      setConnector({ ...data });
    }
    // eslint-disable-next-line
  }, [clientConnectorReducer]);

  // Stop polling once the agent reports a terminal status for the connector under test.
  useEffect(() => {
    if (onPremTestPhase !== "polling") return;
    if (RESOLVED_TEST_STATUSES.includes(connector.onPremConnTestStatus)) {
      setOnPremTestPhase("resolved");
      stopPolling();
      // A resolved test means the agent just checked in and reported its current version. Re-fetch
      // once (edit-safe merge) so the displayed Connector Version reflects it — previously the new
      // version only appeared after navigating away and back.
      refreshVersionRef.current = true;
      dispatch(clientConnectorActions.get(buildConnectorQuery()));
    }
    // eslint-disable-next-line
  }, [connector.onPremConnTestStatus, onPremTestPhase]);

  // Clean up the poll timers on unmount.
  useEffect(() => () => {
    stopPolling();
    stopLogPolling();
  }, []);

  useEffect(() => {
    if (clientConnectorReducer.success) {
      showToast(connector.connectorId ? "Connector updated successfully" : "Connector created successfully", "success");
      if (onSaveSuccess) {
        onSaveSuccess();
      } else {
        navigate("/connector");
      }
    }
    // eslint-disable-next-line
  }, [clientConnectorReducer.success]);

  useEffect(() => {
    // Update selected system config when system changes
    if (connector.system && runbookSystemReducer.response) {
      const apiSystemConfig = runbookSystemReducer.response.find(
        (s) => s.system === connector.system
      );

      if (apiSystemConfig) {
        // Use API config (handle both camelCase and PascalCase)
        const config = {
          requireLoginEndpoint: apiSystemConfig.requireLoginEndpoint ?? apiSystemConfig.RequireLoginEndpoint,
          requireCompanyId: apiSystemConfig.requireCompanyId ?? apiSystemConfig.RequireCompanyId,
          requireDelimiter: apiSystemConfig.requireDelimiter ?? apiSystemConfig.RequireDelimiter,
          requireSenderId: apiSystemConfig.requireSenderId ?? apiSystemConfig.RequireSenderId,
          requireSenderPassword: apiSystemConfig.requireSenderPassword ?? apiSystemConfig.RequireSenderPassword,
          requireUserId: apiSystemConfig.requireUserId ?? apiSystemConfig.RequireUserId,
          requireUserPassword: apiSystemConfig.requireUserPassword ?? apiSystemConfig.RequireUserPassword,
          requireClientId: apiSystemConfig.requireClientId ?? apiSystemConfig.RequireClientId,
          requireClientSecret: apiSystemConfig.requireClientSecret ?? apiSystemConfig.RequireClientSecret,
          requireTenantId: apiSystemConfig.requireTenantId ?? apiSystemConfig.RequireTenantId,
          requireScope: apiSystemConfig.requireScope ?? apiSystemConfig.RequireScope,
          requireRefreshToken: apiSystemConfig.requireRefreshToken ?? apiSystemConfig.RequireRefreshToken,
          requireVersion: apiSystemConfig.requireVersion ?? apiSystemConfig.RequireVersion,
          requireHeaderRow: apiSystemConfig.requireHeaderRow ?? apiSystemConfig.RequireHeaderRow,
        };
        setSelectedSystemConfig(config);
      } else {
        setSelectedSystemConfig(null);
      }
    } else {
      setSelectedSystemConfig(null);
    }
  }, [connector.system, runbookSystemReducer.response]);

  const fieldNameToPascalCase = (fieldName) => {
    // Convert camelCase to PascalCase for API (only for secret fields)
    const mapping = {
      senderPassword: "SenderPassword",
      userPassword: "UserPassword",
      clientSecret: "ClientSecret",
      refreshToken: "RefreshToken",
    };
    return mapping[fieldName] || fieldName;
  };

  const handleUpdateSecret = async (fieldName, secretValue) => {
    try {
      const requestPayload = {
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
        connectorId: connector.connectorNumber || connector.connectorId,
        fieldType: fieldNameToPascalCase(fieldName),
        secretKey: connector[fieldName] || "", // Pass existing GUID if updating, empty string if new
        secretValue: secretValue,
      };

      const response = await secretService.updateConnectorSecret(requestPayload);

      // Response is wrapped in Axios response object, actual data is in response.data
      const data = response.data || response;

      // Update the connector with the returned secret GUID
      if (data && data.isSuccessful && data.secretKey) {
        setConnector((c) => ({ ...c, [fieldName]: data.secretKey }));
      }
    } catch (error) {
      // You might want to show an error toast/alert here
    }
  };

  const handleViewSecret = async (fieldName) => {
    try {
      const secretKeyValue = connector[fieldName];

      // Check if the value is a GUID (Key Vault secret name) or an actual secret
      const isGuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(secretKeyValue);

      if (!isGuid) {
        // This is an old connector with the secret stored directly in the database
        return secretKeyValue;
      }

      const requestData = {
        clientId: props.user.clientId,
        clientGuid: props.user.clientGuid,
        connectorId: connector.connectorNumber || connector.connectorId,
        secretKey: secretKeyValue,
      };

      const response = await secretService.retrieveConnectorSecret(requestData);

      // Response is wrapped in Axios response object, actual data is in response.data
      const data = response.data || response;

      if (data && data.isSuccessful && data.secretValue) {
        return data.secretValue;
      } else if (data && !data.isSuccessful) {
        const errorMsg = data.responseMessage || "Unknown error";
        return "Error: " + errorMsg;
      }
      return "Error: Unable to retrieve secret";
    } catch (error) {
      return "Error: " + (error.message || "Unable to retrieve secret");
    }
  };

  const handleSaveConnector = () => {
    let connectorData = connector;
    connectorData.clientId = props.user.clientId;
    connectorData.clientGuid = props.user.clientGuid;
    if (connector.connectorId) {
      dispatch(clientConnectorActions.update(connectorData));
    } else {
      dispatch(clientConnectorActions.create(connectorData));
    }
  };

  const operation = !connector.connectorId ? "Add" : "Update";

  // Check permissions
  const isEditMode = !!connector.connectorId;
  const hasEditPermission = hasPermission("connectors.edit");
  const canCreateConnector = props.user.isAdmin || props.user.isSuperUser;

  const canAccessForm = isEditMode ? hasEditPermission : canCreateConnector;

  // Only show the skeleton on the FIRST load (before the connector is populated). Background
  // refreshes during OnPrem test polling also flip the global `loading` flag, and swapping the
  // whole form for the skeleton on every poll made the screen blink — so ignore `loading` once
  // the connector is loaded.
  const showFormSkeleton = clientConnectorReducer.loading && !connector.connectorId;

  // The global `loading` flag is also set by the background test-status poll, so only treat it as
  // a save-in-progress when we're NOT actively polling — otherwise the Save button would flicker
  // to a spinner every 4s during a test.
  const isSaving = clientConnectorReducer.loading && onPremTestPhase !== "polling";

  return (
    <>
      {!hideTitle && (
        <Title
          text={`${operation} Connector`}
          icon={<FaDatabase className="mb-1" />}
        />
      )}

      {showFormSkeleton && <SkeletonForm fields={12} columns={2} />}

      {!showFormSkeleton && (
        <>
          {!canAccessForm && (
            <Alert variant="danger">
              You do not have permission to {isEditMode ? "edit" : "create"} connectors. Please contact your administrator.
            </Alert>
          )}

          {canAccessForm && !clientConnectorReducer.success && (
            <>
              {!clientConnectorReducer.loading &&
                clientConnectorReducer.error && (
                  <Alert variant={"danger"}>
                    An error has occurred. Please try again later.
                  </Alert>
                )}

              <Form.Group controlId="formConnectorId" className="d-none">
                <Form.Control
                  type="text"
                  value={connector.connectorId}
                  onChange={(e) =>
                    setConnector((c) => ({ ...c, connectorId: e.target.value }))
                  }
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group controlId="formConnectorNumber" className="mb-3">
                    <Form.Label>Connector Number</Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={connector.connectorNumber}
                      onChange={(e) =>
                        setConnector((c) => ({
                          ...c,
                          connectorNumber: e.target.value,
                        }))
                      }
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formSystem" className="mb-3">
                    <Form.Label>System</Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      {connector.system && (
                        <SystemIcon
                          system={connector.system}
                          iconUrl={
                            runbookSystemReducer.response?.find(
                              (s) => s.system === connector.system
                            )?.iconUrl
                          }
                          size={48}
                        />
                      )}
                      <div className="flex-grow-1">
                        <RunbookSystemDropdown
                          value={connector.system}
                          onChange={(value) =>
                            setConnector((c) => ({ ...c, system: value }))
                          }
                          size="sm"
                        />
                      </div>
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group controlId="formDescription" className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value={connector.description}
                  onChange={(e) =>
                    setConnector((c) => ({ ...c, description: e.target.value }))
                  }
                  placeholder="Brief description of this connector"
                />
              </Form.Group>

              {selectedSystemConfig?.requireLoginEndpoint && (
                <Form.Group controlId="formLoginEndpoint" className="mb-3">
                  <Form.Label>Login Endpoint</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={connector.loginEndpoint}
                    onChange={(e) =>
                      setConnector((c) => ({
                        ...c,
                        loginEndpoint: e.target.value,
                      }))
                    }
                    placeholder="API endpoint URL"
                  />
                </Form.Group>
              )}

              <Row>
                {selectedSystemConfig?.requireCompanyId && (
                  <Col md={6}>
                    <Form.Group controlId="formCompanyId" className="mb-3">
                      <Form.Label>Company Id</Form.Label>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={connector.companyId}
                        onChange={(e) =>
                          setConnector((c) => ({ ...c, companyId: e.target.value }))
                        }
                      />
                    </Form.Group>
                  </Col>
                )}
                {selectedSystemConfig?.requireDelimiter && (
                  <Col md={6}>
                    <Form.Group controlId="delimiter" className="mb-3">
                      <Form.Label>Delimiter</Form.Label>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={connector.delimiter}
                        onChange={(e) =>
                          setConnector((c) => ({
                            ...c,
                            delimiter: e.target.value,
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>
                )}
                {selectedSystemConfig?.requireHeaderRow && (
                  <Col md={6}>
                    <Form.Group controlId="headerRow" className="mb-3">
                      <Form.Label>Header Row</Form.Label>
                      <Form.Check
                        type="checkbox"
                        label="File contains a header row"
                        checked={!!connector.headerRow}
                        onChange={(e) =>
                          setConnector((c) => ({
                            ...c,
                            headerRow: e.target.checked,
                          }))
                        }
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <Row>
                {selectedSystemConfig?.requireSenderId && (
                  <Col md={6}>
                    <Form.Group controlId="formSenderId" className="mb-3">
                      <Form.Label>Sender Id</Form.Label>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={connector.senderId}
                        onChange={(e) =>
                          setConnector((c) => ({ ...c, senderId: e.target.value }))
                        }
                        autoComplete="new-password"
                      />
                    </Form.Group>
                  </Col>
                )}
                {selectedSystemConfig?.requireSenderPassword && (
                  <Col md={6}>
                    <Form.Group controlId="formSenderPassword" className="mb-3">
                      <Form.Label>Sender Password</Form.Label>
                      <SecretInput
                        size="sm"
                        value={connector.senderPassword}
                        onUpdateSecret={(secretValue) => handleUpdateSecret('senderPassword', secretValue)}
                        onViewSecret={() => handleViewSecret('senderPassword')}
                        label="Sender Password"
                        isAdmin={props.user.isAdmin}
                        disabled={!connector.connectorNumber}
                        canViewSecrets={props.user.isSuperUser || props.user.isProductTeam}
                        canEditSecrets={hasPermission("connectors.editSecrets")}
                      />
                    </Form.Group>
                  </Col>
                )}
                {selectedSystemConfig?.requireUserId && (
                  <Col md={6}>
                    <Form.Group controlId="formUserId" className="mb-3">
                      <Form.Label>User Id</Form.Label>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={connector.userId}
                        onChange={(e) =>
                          setConnector((c) => ({ ...c, userId: e.target.value }))
                        }
                        autoComplete="new-password"
                      />
                    </Form.Group>
                  </Col>
                )}
                {selectedSystemConfig?.requireUserPassword && (
                  <Col md={6}>
                    <Form.Group controlId="formUserPassword" className="mb-3">
                      <Form.Label>User Password</Form.Label>
                      <SecretInput
                        size="sm"
                        value={connector.userPassword}
                        onUpdateSecret={(secretValue) => handleUpdateSecret('userPassword', secretValue)}
                        onViewSecret={() => handleViewSecret('userPassword')}
                        label="User Password"
                        isAdmin={props.user.isAdmin}
                        disabled={!connector.connectorNumber}
                        canViewSecrets={props.user.isSuperUser || props.user.isProductTeam}
                        canEditSecrets={hasPermission("connectors.editSecrets")}
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <Row>
                {selectedSystemConfig?.requireClientId && (
                  <Col md={6}>
                    <Form.Group controlId="formOAuthClientId" className="mb-3">
                      <Form.Label>OAuth ClientId</Form.Label>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={connector.oAuthClientId}
                        onChange={(e) =>
                          setConnector({ ...connector, oAuthClientId: e.target.value })
                        }
                        disabled={!connector.connectorNumber}
                      />
                    </Form.Group>
                  </Col>
                )}
                {selectedSystemConfig?.requireClientSecret && (
                  <Col md={6}>
                    <Form.Group controlId="formClientSecret" className="mb-3">
                      <Form.Label>Client Secret</Form.Label>
                      <SecretInput
                        size="sm"
                        value={connector.clientSecret}
                        onUpdateSecret={(secretValue) => handleUpdateSecret('clientSecret', secretValue)}
                        onViewSecret={() => handleViewSecret('clientSecret')}
                        label="Client Secret"
                        isAdmin={props.user.isAdmin}
                        disabled={!connector.connectorNumber}
                        canViewSecrets={props.user.isSuperUser || props.user.isProductTeam}
                        canEditSecrets={hasPermission("connectors.editSecrets")}
                      />
                    </Form.Group>
                  </Col>
                )}
                {selectedSystemConfig?.requireTenantId && (
                  <Col md={6}>
                    <Form.Group controlId="formOAuthTenantId" className="mb-3">
                      <Form.Label>OAuth TenantId</Form.Label>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={connector.tenantId}
                        onChange={(e) =>
                          setConnector((c) => ({ ...c, tenantId: e.target.value }))
                        }
                      />
                    </Form.Group>
                  </Col>
                )}
                {selectedSystemConfig?.requireRefreshToken && (
                  <Col md={6}>
                    <Form.Group controlId="formRefreshToken" className="mb-3">
                      <Form.Label>Refresh Token</Form.Label>
                      <SecretInput
                        size="sm"
                        value={connector.refreshToken}
                        onUpdateSecret={(secretValue) => handleUpdateSecret('refreshToken', secretValue)}
                        onViewSecret={() => handleViewSecret('refreshToken')}
                        label="Refresh Token"
                        isAdmin={props.user.isAdmin}
                        disabled={!connector.connectorNumber}
                        canViewSecrets={props.user.isSuperUser || props.user.isProductTeam}
                        canEditSecrets={hasPermission("connectors.editSecrets")}
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>

              {selectedSystemConfig?.requireScope && (
                <Form.Group controlId="formScope" className="mb-3">
                  <Form.Label>Scope</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={connector.scope}
                    onChange={(e) =>
                      setConnector((c) => ({
                        ...c,
                        scope: e.target.value,
                      }))
                    }
                    placeholder="e.g., openid profile email"
                  />
                </Form.Group>
              )}

              {selectedSystemConfig?.requireVersion && (
                <Form.Group controlId="formVersion" className="mb-3">
                  <Form.Label>Version</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={connector.version}
                    onChange={(e) =>
                      setConnector((c) => ({
                        ...c,
                        version: e.target.value,
                      }))
                    }
                    placeholder="API version"
                  />
                </Form.Group>
              )}

              {isOnPrem && (
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title as="h6">
                      {isEditMode ? "Agent / Connection Status" : "On-Premise Agent"}
                    </Card.Title>

                    {!isEditMode && (
                      <div className="text-muted small mb-2">
                        Install this agent on the client's server so it can process runbooks for this
                        connector. Download the current published installer and run it there.
                      </div>
                    )}

                    {isEditMode && (
                      <>

                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="text-muted small">Connector Version:</span>
                      <span className="small">
                        {connector.onPremConnectorVersion || "Unknown"}
                      </span>
                    </div>

                    {props.user.isSuperUser && (
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <Form.Check
                          type="switch"
                          id="onprem-autoupdate-switch"
                          label="Auto-update enabled"
                          checked={!connector.onPremAutoUpdateDisabled}
                          disabled={togglingAutoUpdate}
                          onChange={handleToggleAutoUpdate}
                        />
                        {togglingAutoUpdate && <Spinner size="sm" />}
                        <span className="text-muted small">
                          (applies to this client's OnPrem agent)
                        </span>
                      </div>
                    )}

                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="text-muted small">Last Test:</span>
                      {lastTestBadge(connector.onPremConnTestStatus)}
                      {connector.onPremConnTestTime && (
                        <span className="text-muted small">
                          {formatTestTime(connector.onPremConnTestTime)}
                        </span>
                      )}
                    </div>

                    {connector.onPremConnTestMessage && (
                      <div className="text-muted small mb-2">
                        {connector.onPremConnTestMessage}
                      </div>
                    )}

                    {clientConnectorReducer.testAuthError && (
                      <div className="text-danger small mb-2">
                        <MdGppBad className="me-1 mb-1" />
                        Failed to dispatch the test: {clientConnectorReducer.testAuthError}
                      </div>
                    )}

                    {onPremTestPhase === "polling" && (
                      <div className="text-primary small mb-2">
                        <Spinner size="sm" className="me-2" />
                        Test dispatched to the on-prem agent. Waiting for a response (up to 60
                        seconds)...
                      </div>
                    )}

                    {onPremTestPhase === "timeout" && (
                      <div className="text-warning small mb-2">
                        The connector hasn't responded within 60 seconds. It may be offline. The
                        status will stay <strong>Pending</strong> and update automatically once the
                        agent responds.
                      </div>
                    )}

                    {onPremTestPhase === "resolved" &&
                      connector.onPremConnTestStatus === "Success" && (
                        <div className="text-success small mb-2">
                          <MdGppGood className="me-1 mb-1" />
                          Connection successful!
                        </div>
                      )}

                    {onPremTestPhase === "resolved" &&
                      ["Failed", "NoResponse"].includes(
                        connector.onPremConnTestStatus
                      ) && (
                        <div className="text-danger small mb-2">
                          <MdGppBad className="me-1 mb-1" />
                          {connector.onPremConnTestStatus === "NoResponse"
                            ? "The connector did not respond."
                            : "Connection failed."}
                        </div>
                      )}

                    {clientConnectorReducer.pushOnPremUpdateSuccess && !pushDismissed && (
                      <Alert
                        variant="success"
                        dismissible
                        onClose={() => setPushDismissed(true)}
                        className="py-2"
                      >
                        Update pushed to the OnPrem connector. The agent will install the latest
                        signed build on its next check-in.
                      </Alert>
                    )}
                    {clientConnectorReducer.pushOnPremUpdateError && !pushDismissed && (
                      <Alert
                        variant="danger"
                        dismissible
                        onClose={() => setPushDismissed(true)}
                        className="py-2"
                      >
                        {clientConnectorReducer.pushOnPremUpdateError}
                      </Alert>
                    )}

                    {logPhase === "polling" && (
                      <div className="text-primary small mb-2">
                        <Spinner size="sm" className="me-2" />
                        Collecting the agent's log. Waiting for a response (up to 90 seconds)...
                      </div>
                    )}

                    {logPhase === "ready" && (
                      <div className="text-success small mb-2">
                        <MdArticle className="me-1 mb-1" />
                        Log ready — your download should start automatically.
                      </div>
                    )}

                    {logPhase === "timeout" && (
                      <div className="text-warning small mb-2">
                        The connector hasn't returned its log within 90 seconds. It may be offline.
                        Try again once it's reachable.
                      </div>
                    )}

                    {logPhase === "failed" && logError && (
                      <div className="text-danger small mb-2">
                        <MdGppBad className="me-1 mb-1" />
                        {logError}
                      </div>
                    )}

                      </>
                    )}

                    <div className="d-flex gap-2 flex-wrap">
                      {isEditMode && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleTestConnection}
                          disabled={onPremTestPhase === "polling"}
                        >
                          <MdGppGood className="mb-1 me-2" />
                          Test Connection
                        </Button>
                      )}

                      {isEditMode && props.user.isSuperUser && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleDownloadServerLog}
                          disabled={logPhase === "polling"}
                          title="Collect this OnPrem agent's recent Event Log and download it (super users only)"
                        >
                          {logPhase === "polling" ? (
                            <Spinner size="sm" className="me-2" />
                          ) : (
                            <MdArticle className="mb-1 me-2" />
                          )}
                          Download Server Log
                        </Button>
                      )}

                      {props.user.isSuperUser && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleDownloadInstaller}
                          disabled={downloadingInstaller}
                          title="Download the current published installer to set up the client's agent (super users only)"
                        >
                          {downloadingInstaller ? (
                            <Spinner size="sm" className="me-2" />
                          ) : (
                            <MdFileDownload className="mb-1 me-2" />
                          )}
                          Download Installer
                        </Button>
                      )}

                      {props.user.isSuperUser && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleCopyInstallerLink}
                          disabled={copyingLink}
                          title="Copy a time-limited download link to send to the client (super users only)"
                        >
                          {copyingLink ? (
                            <Spinner size="sm" className="me-2" />
                          ) : (
                            <MdContentCopy className="mb-1 me-2" />
                          )}
                          {installerLinkCopied ? "Link copied!" : "Copy download link"}
                        </Button>
                      )}

                      {isEditMode && props.user.isSuperUser && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handlePushUpdate}
                          disabled={clientConnectorReducer.pushingOnPremUpdate}
                          title="Push an update to this OnPrem connector's agent (super users only)"
                        >
                          {clientConnectorReducer.pushingOnPremUpdate ? (
                            <Spinner size="sm" className="me-2" />
                          ) : (
                            <MdSystemUpdateAlt className="mb-1 me-2" />
                          )}
                          Push OnPrem Update
                        </Button>
                      )}
                    </div>

                    {downloadInstallerError && (
                      <div className="text-danger small mt-2">{downloadInstallerError}</div>
                    )}

                    {installerLinkCopied && installerLinkExpiry && (
                      <div className="text-muted small mt-2">
                        Link copied — valid until {formatTestTime(installerLinkExpiry)}. Send it to the
                        client before then; after that it must be re-generated.
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  onClick={() => (onCancel ? onCancel() : navigate(-1))}
                >
                  Cancel
                </Button>

                <Button
                  variant="primary"
                  onClick={handleSaveConnector}
                  disabled={isSaving}
                >
                  {!isSaving ? (
                    <>{operation} Connector</>
                  ) : (
                    <Spinner size="sm" />
                  )}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

export default ConnectorForm;

